import React, { useEffect, useState } from "react";
import cx from "classnames";
import PageWrapper from "../PageWrapper";
import SEO from "../SEO";
import { LegalBlock, LegalDocument } from "../../data/legal";

interface Props {
  path: string;
  document: LegalDocument;
}

const BlockRenderer: React.FC<{ block: LegalBlock }> = ({ block }) => {
  switch (block.type) {
    case "p":
      return <p className="mb-4 text-[15px] leading-7 text-gray-700">{block.text}</p>;
    case "note":
      return (
        <p className="mb-4 border-l-4 border-primary bg-primary3 px-4 py-3 text-[15px] font-medium leading-7 text-gray-800">
          {block.text}
        </p>
      );
    case "subheading":
      return (
        <h3 className="mt-6 mb-3 text-base font-bold uppercase tracking-wide text-gray-900">
          {block.text}
        </h3>
      );
    case "list":
      return (
        <ul className="mb-4 space-y-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex text-[15px] leading-7 text-gray-700">
              <span className="mr-3 mt-[11px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "contact":
      return (
        <div className="space-y-1 text-[15px] leading-7 text-gray-700">
          {block.lines.map((line, i) => (
            <p key={i} className="font-semibold text-gray-900">
              {line}
            </p>
          ))}
          <p>
            Email:{" "}
            <a href={`mailto:${block.email}`} className="text-primary hover:underline">
              {block.email}
            </a>
          </p>
          <p>
            Website:{" "}
            <a
              href={block.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {block.website}
            </a>
          </p>
        </div>
      );
    default:
      return null;
  }
};

const LegalPage: React.FC<Props> = ({ path, document: doc }) => {
  const { title, effectiveDate, intro, sections } = doc;
  const [activeId, setActiveId] = useState<string>(sections[0]?.id);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );

    sections.forEach(section => {
      const el = window.document.getElementById(section.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sections]);

  return (
    <PageWrapper path={path}>
      <SEO pageUrl={path} title={`${title} | Matrix Rental Solutions`} />
      <main className="mx-auto max-w-6xl px-2.5 py-10 font-primary md:py-14">
        <header className="mb-10 border-b border-borderColor pb-8">
          <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">{title}</h1>
          <p className="mt-3 text-sm font-medium text-primary">
            Effective Date: {effectiveDate}
          </p>
          <p className="mt-5 max-w-3xl text-[15px] leading-7 text-gray-700">{intro}</p>
        </header>

        <div className="flex flex-col md:flex-row md:gap-12">
          <aside className="hidden md:block md:w-72 md:flex-shrink-0">
            <nav className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
              <ul className="border-l border-borderColor">
                {sections.map((section, index) => {
                  const isActive = activeId === section.id;
                  return (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className={cx(
                          "-ml-px block border-l-2 py-2 pl-4 text-sm transition-colors",
                          isActive
                            ? "border-primary font-semibold text-primary"
                            : "border-transparent text-gray-500 hover:text-gray-900"
                        )}
                      >
                        {index + 1}. {section.title}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          <div className="min-w-0 flex-1">
            {sections.map((section, index) => (
              <section
                key={section.id}
                id={section.id}
                className="mb-12 scroll-mt-28 last:mb-0"
              >
                <h2 className="mb-4 text-xl font-bold text-gray-900 md:text-2xl">
                  {index + 1}. {section.title}
                </h2>
                {section.blocks.map((block, blockIndex) => (
                  <BlockRenderer key={blockIndex} block={block} />
                ))}
              </section>
            ))}
          </div>
        </div>
      </main>
    </PageWrapper>
  );
};

export default React.memo(LegalPage);
